/**
 * ABTestSandbox — collapsible "A/B Test Sandbox" panel below the editor.
 *
 * POST /reasoning/test runs BOTH the draft prompt and the currently-active
 * prompt against every query (2x LIVE Grok calls per query, hardcoded
 * grok-3-mini @ temp 0, 10s timeout each) — this is real production model
 * spend/latency, not a dry run, so the warning banner is persistent (shown
 * whenever the panel is expanded, not just after Run is clicked) and the
 * exact call count is always visible before the user commits.
 */

import React, { useEffect, useState } from 'react';
import {
  Box,
  Typography,
  Accordion,
  AccordionSummary,
  AccordionDetails,
  TextField,
  Button,
  Alert,
  Table,
  TableHead,
  TableRow,
  TableCell,
  TableBody,
  Chip,
  CircularProgress,
} from '@mui/material';
import ExpandMoreIcon from '@mui/icons-material/ExpandMore';
import ScienceIcon from '@mui/icons-material/Science';
import PlayArrowIcon from '@mui/icons-material/PlayArrow';
import EmptyState from '../../common/EmptyState';
import { colors, typography } from '../../../theme/designTokens';
import { quillApi, QuillApiError, ReasoningConfigKey, ReasoningTestResult } from '../../../services/quillAdminService';
import { describeReasoningError } from './reasoningManifest';

const MAX_QUERIES = 20;

export interface ABTestSandboxProps {
  configKey: ReasoningConfigKey;
  /** Current editor buffer's prompt body — used only to seed the sandbox draft when the selected key changes. */
  editorPromptBody: string;
  hasActiveVersion: boolean;
}

const ABTestSandbox: React.FC<ABTestSandboxProps> = ({ configKey, editorPromptBody, hasActiveVersion }) => {
  const [expanded, setExpanded] = useState(false);
  const [draftPrompt, setDraftPrompt] = useState(editorPromptBody);
  const [queriesText, setQueriesText] = useState('');
  const [results, setResults] = useState<ReasoningTestResult[] | null>(null);
  const [running, setRunning] = useState(false);
  const [error, setError] = useState<QuillApiError | Error | null>(null);

  // Re-seed the sandbox draft only when the *selected key* changes (independent
  // editing afterwards — further edits in the editor above must not clobber it).
  useEffect(() => {
    setDraftPrompt(editorPromptBody);
    setResults(null);
    setError(null);
    setQueriesText('');
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [configKey]);

  const queries = queriesText
    .split('\n')
    .map((q) => q.trim())
    .filter(Boolean);
  const n = queries.length;
  const overLimit = n > MAX_QUERIES;
  const canRun = n > 0 && !overLimit && !running;

  const handleRun = () => {
    setRunning(true);
    setError(null);
    quillApi.reasoning
      .test({ draft_prompt: draftPrompt, config_key: configKey, test_queries: queries })
      .then((res) => setResults(res.results))
      .catch((e) => setError(e))
      .finally(() => setRunning(false));
  };

  const isNoGrokKey = error instanceof QuillApiError && error.code === 'NO_GROK_KEY';

  return (
    <Accordion
      expanded={expanded}
      onChange={(_, isExp) => setExpanded(isExp)}
      sx={{ mt: 3, border: '1px solid', borderColor: colors.gray[200], boxShadow: 'none', '&:before': { display: 'none' } }}
    >
      <AccordionSummary expandIcon={<ExpandMoreIcon />} aria-controls="ab-test-sandbox-content">
        <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
          <ScienceIcon sx={{ color: colors.warmGold[500] }} fontSize="small" />
          <Typography sx={{ fontWeight: 700, fontFamily: typography.fonts.heading }}>A/B Test Sandbox</Typography>
        </Box>
      </AccordionSummary>
      <AccordionDetails id="ab-test-sandbox-content">
        <Typography sx={{ fontSize: '0.7rem', fontWeight: 700, textTransform: 'uppercase', letterSpacing: '0.06em', color: 'text.secondary', mb: 0.5 }}>
          Config key
        </Typography>
        <Typography sx={{ fontFamily: typography.fonts.mono, fontSize: '0.8rem', mb: 2 }}>{configKey}</Typography>

        <TextField
          label="Draft prompt (independently editable from the editor above)"
          value={draftPrompt}
          onChange={(e) => setDraftPrompt(e.target.value)}
          fullWidth
          multiline
          minRows={6}
          sx={{ mb: 2 }}
          inputProps={{ style: { fontFamily: typography.fonts.mono, fontSize: '0.8rem' } }}
        />

        <TextField
          label="Test queries (one per line)"
          value={queriesText}
          onChange={(e) => setQueriesText(e.target.value)}
          fullWidth
          multiline
          minRows={4}
          error={overLimit}
          helperText={overLimit ? `Too many queries — remove ${n - MAX_QUERIES} to run.` : ' '}
          sx={{ mb: 0.5 }}
        />
        <Typography sx={{ fontSize: '0.75rem', color: overLimit ? colors.error.main : 'text.secondary', mb: 2 }}>
          {n}/{MAX_QUERIES}
        </Typography>

        {!hasActiveVersion && (
          <Alert severity="info" sx={{ mb: 2 }}>
            This key has no active version — active column will error; comparing draft only.
          </Alert>
        )}

        <Alert severity="warning" sx={{ mb: 2 }}>
          Runs LIVE Grok calls: {Math.max(n, 0) * 2} requests (draft + active per query), grok-3-mini @ temp 0, up to ~10s each.
        </Alert>

        <Button
          variant="contained"
          startIcon={running ? <CircularProgress size={16} color="inherit" /> : <PlayArrowIcon />}
          disabled={!canRun}
          onClick={handleRun}
          sx={{
            textTransform: 'none',
            backgroundColor: colors.deepTeal[500],
            '&:hover': { backgroundColor: colors.deepTeal[600] },
          }}
        >
          {running ? 'Running…' : 'Run'}
        </Button>

        {error && !isNoGrokKey && (
          <Alert severity="error" sx={{ mt: 2 }}>
            {describeReasoningError(error)}
          </Alert>
        )}

        {isNoGrokKey && (
          <Box sx={{ mt: 2 }}>
            <EmptyState
              icon="key"
              title="No Grok API key configured"
              description="The local quill-admin-backend did not load a Grok API key (secret quill/grok/api-key)."
            />
          </Box>
        )}

        {results && results.length > 0 && (
          <Table size="small" sx={{ mt: 3 }}>
            <TableHead>
              <TableRow>
                <TableCell>Query</TableCell>
                <TableCell>Draft</TableCell>
                <TableCell>Active</TableCell>
                <TableCell>Changed</TableCell>
              </TableRow>
            </TableHead>
            <TableBody>
              {results.map((r, i) => (
                <TableRow
                  key={`${r.query}-${i}`}
                  sx={
                    r.changed
                      ? { backgroundColor: colors.warmGold[50], borderLeft: `3px solid ${colors.warmGold[500]}` }
                      : undefined
                  }
                >
                  <TableCell sx={{ maxWidth: 240 }}>{r.query}</TableCell>
                  <TableCell>
                    {r.draft.verdict === 'ERROR' ? (
                      <Chip label="ERROR" size="small" sx={{ backgroundColor: colors.error[50], color: colors.error.dark }} />
                    ) : (
                      `${r.draft.verdict}${r.draft.category ? ` / ${r.draft.category}` : ''} (${r.draft.confidence})`
                    )}
                  </TableCell>
                  <TableCell>
                    {!r.active ? (
                      <Typography sx={{ fontSize: '0.75rem', color: 'text.disabled', fontStyle: 'italic' }}>
                        no active version
                      </Typography>
                    ) : r.active.verdict === 'ERROR' ? (
                      <Chip label="ERROR" size="small" sx={{ backgroundColor: colors.error[50], color: colors.error.dark }} />
                    ) : (
                      `${r.active.verdict}${r.active.category ? ` / ${r.active.category}` : ''} (${r.active.confidence})`
                    )}
                  </TableCell>
                  <TableCell>
                    {r.changed === true && (
                      <Chip label="Changed" size="small" sx={{ backgroundColor: colors.warmGold[500], color: colors.gray[900], fontWeight: 600 }} />
                    )}
                    {r.changed === false && <Chip label="Same" size="small" variant="outlined" />}
                    {r.changed == null && (
                      <Typography sx={{ fontSize: '0.75rem', color: 'text.disabled' }}>—</Typography>
                    )}
                  </TableCell>
                </TableRow>
              ))}
            </TableBody>
          </Table>
        )}
      </AccordionDetails>
    </Accordion>
  );
};

export default ABTestSandbox;
